<script setup lang="ts">
  import { computed, nextTick, reactive, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { injectStrict } from '../api/injection.ts';
  import { ApiKey } from '../api/symbols.ts';
  import InputCurrency from '../components/forms/InputCurrency.vue';
  import Loading from '../components/Loading.vue';
  import AxiosErrorHandler from '../components/AxiosErrorHandler.vue';
  import SectionTitle from '../components/SectionTitle.vue';
  import { withCatch } from '@/utils/await-safe.ts';

  import { Switch } from '@/components/ui/switch';
  import { Label } from '@/components/ui/label';
  import { Textarea } from '@/components/ui/textarea';
  import { Checkbox } from '@/components/ui/checkbox';
  import { Button } from '@/components/ui/button';
  import { Alert, AlertDescription } from '@/components/ui/alert';

  interface EventContribution {
    id: string;
    code: string;
    contributions: Array<EventContributionItem>;
    agreeToTerms: boolean;
    notesToMod: string;
  }

  interface EventInformation {
    name: string;
    virtualAssistants: VirtualAssistant[];
  }

  interface EventContributionItem {
    id: string;
    name: string;
    amount: number;
    comment: string;
  }

  interface VirtualAssistant {
    id: string;
    name: string;
  }

  const equalDistribution = ref(true);
  const busy = ref(false);
  const initialLoad = ref(false);
  const equalDistributionAmount = ref(0);
  const equalDistributionComment = ref('');
  const errors = ref<any[]>([]);
  const contributionsWithErrors = ref<Set<string>>(new Set());
  const equalDistributionHasError = ref(false);

  watch([equalDistributionAmount, equalDistributionComment], () => {
    // set all contributions to equal distribution amount
    formData.contributions.forEach((contribution) => {
      contribution.amount = equalDistributionAmount.value;
      contribution.comment = equalDistributionComment.value;
    });
  });

  const formData = reactive<EventContribution>({
    id: '',
    code: '',
    notesToMod: '',
    contributions: [],
    agreeToTerms: false,
  });

  const event = reactive<EventInformation>({
    name: '',
    virtualAssistants: [],
  });

  const route = useRoute();
  const router = useRouter();
  const api = injectStrict(ApiKey);
  // get search params from window
  const code = route.query.code ?? route.query['amp;code'] ?? undefined;
  const eventId = route.query.id;

  // console.log(route.query, code, eventId);

  const getEventInformation = async () => {
    if (!code || !eventId) {
      return await router.push('/error');
    }

    const config = {
      params: {
        id: eventId,
        code: code,
      },
    };

    initialLoad.value = true;
    const [error, resp] = await withCatch(api.get(`/api/event`, config));
    initialLoad.value = false;

    if (error || !resp) {
      return await router.push('/error');
    }

    formData.code = `${code}`;
    formData.id = `${eventId}`;
    event.name = resp.data.name;
    event.virtualAssistants = resp.data.virtualAssistants;

    // add each va to the contributions available
    event.virtualAssistants.forEach((va) => {
      formData.contributions.push({
        id: va.id,
        name: va.name,
        amount: 0,
        comment: '',
      });
    });
  };

  const totalContributionAmount = computed(() => {
    let total = 0;
    formData.contributions.forEach((contribution) => {
      total += contribution.amount;
    });

    // format as currency, usd using international formatter
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(total);
  });

  const clearEqualDistributionError = () => {
    equalDistributionHasError.value = false;
  };

  const clearContributionError = (contributionId: string) => {
    contributionsWithErrors.value.delete(contributionId);
    contributionsWithErrors.value = new Set(contributionsWithErrors.value);
  };

  const submit = async () => {
    // Clear previous errors
    errors.value = [];
    contributionsWithErrors.value.clear();
    equalDistributionHasError.value = false;

    // Validate that each contribution has a comment
    const contributionsWithoutComments = formData.contributions.filter((contribution) => !contribution.comment || contribution.comment.trim() === '');

    if (contributionsWithoutComments.length > 0) {
      errors.value = ['Message to Virtual Assistant is required for all contributions.'];

      // Mark contributions with errors
      if (equalDistribution.value) {
        equalDistributionHasError.value = true;
      } else {
        contributionsWithoutComments.forEach((contribution) => {
          contributionsWithErrors.value.add(contribution.id);
        });
      }

      // Wait for DOM to update with error classes, then scroll to first error field
      await nextTick();
      const firstErrorField = document.querySelector('.ring-red-500');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.scrollTo(0, 0);
      }

      return;
    }

    busy.value = true;

    const [error, resp] = await withCatch(api.post(`/api/event`, formData));
    busy.value = false;

    if (error || !resp) {
      if (error.response && error.response.data) {
        errors.value = error.response.data;
      } else {
        //in some rare cases response is not defined for some reason
        errors.value = ['An error occurred. Please try again.'];
      }
      window.scrollTo(0, 0);
      return;
    }

    busy.value = false;
    await router.push('/success');
  };

  // Faker function to populate virtual assistants for testing
  const populateFakeVirtualAssistants = (count: number = 3) => {
    const fakeNames = ['Maria Santos', 'Juan Dela Cruz', 'Catherine Reyes', 'Miguel Rodriguez', 'Sofia Garcia', 'Carlos Martinez', 'Isabella Cruz', 'Diego Fernandez', 'Valentina Lopez', 'Alejandro Gonzalez'];

    event.name = 'Annual Company Holiday Party 2025';
    event.virtualAssistants = [];

    for (let i = 0; i < Math.min(count, fakeNames.length); i++) {
      const va: VirtualAssistant = {
        id: `va-${Math.random().toString(36).substring(2, 11)}`,
        name: fakeNames[i],
      };
      event.virtualAssistants.push(va);
    }

    // Populate contributions
    formData.contributions = [];
    event.virtualAssistants.forEach((va) => {
      formData.contributions.push({
        id: va.id,
        name: va.name,
        amount: 0,
        comment: '',
      });
    });
  };

  // In dev mode, use fake data; in production, use real API
  if (import.meta.env.DEV) {
    populateFakeVirtualAssistants(2);
  } else {
    getEventInformation();
  }
</script>

<template>
  <div class="mx-auto mt-4 max-w-xl">
    <div v-if="initialLoad || busy">
      <Loading />
    </div>
    <div v-else>
      <SectionTitle data-testid="event_name">
        <template #default>
          <div>{{ event.name }}</div>
        </template>
        <template #subtitle> Event Contribution Form </template>
      </SectionTitle>

      <AxiosErrorHandler v-if="errors" :errors="errors" />
      <form @submit.prevent="submit">
        <p class="my-4 text-sm text-muted-foreground">100% of your contributions are passed through to your Virtual Assistants. The contribution amount will show up on your next invoice as an additional line item.</p>
        <div v-if="event.virtualAssistants.length > 1" class="mb-6 flex items-center justify-center gap-2 rounded-lg border bg-accent/50 p-4">
          <Switch v-model="equalDistribution" :disabled="busy" />
          <Label class="cursor-pointer font-medium">Equal Distribution</Label>
          <span class="text-xs text-muted-foreground">(Same amount for all VAs)</span>
        </div>
        <div v-if="equalDistribution" class="rounded-lg border bg-card p-6 shadow-sm">
          <div class="space-y-4">
            <div class="flex items-center gap-3 border-b pb-3">
              <div v-if="formData.contributions.length > 1" class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              </div>
              <div v-else class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {{
                  formData.contributions[0]?.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)
                }}
              </div>
              <div>
                <h3 class="text-lg font-semibold">{{ formData.contributions.length > 1 ? 'Equal Distribution' : formData.contributions[0]?.name }}</h3>
                <p v-if="formData.contributions.length > 1" class="text-xs text-muted-foreground m-0">{{ formData.contributions.length }} Virtual Assistants will receive the same amount</p>
              </div>
            </div>
            <div class="space-y-3">
              <div>
                <Label class="mb-2 text-sm font-medium">{{ event.virtualAssistants.length > 1 ? 'Contribution Amount Per VA' : 'Contribution Amount' }}</Label>
                <InputCurrency v-model="equalDistributionAmount" :options="{ currency: 'USD' }" :label="event.virtualAssistants.length > 1 ? 'Contribution Amount Per VA' : 'Contribution Amount'" :disabled="busy" class="max-w-40" />
                <p v-if="formData.contributions.length > 1" class="mt-1.5 text-xs text-muted-foreground">Each VA will receive this amount</p>
              </div>
              <div>
                <Label class="mb-2 text-sm font-medium">{{ formData.contributions.length > 1 ? 'Shared Message' : `Message to ${formData.contributions[0]?.name.split(' ')[0]}` }}</Label>
                <Textarea
                  v-model="equalDistributionComment"
                  aria-label="Message to virtual assistant"
                  :placeholder="formData.contributions.length > 1 ? 'Write a message that will be sent to all Virtual Assistants...' : 'Write a personal message to your Virtual Assistant...'"
                  :disabled="busy"
                  :class="['h-24 resize-none', equalDistributionHasError ? 'ring-2 ring-red-500 border-red-500' : '']"
                  @input="clearEqualDistributionError"
                />
                <p v-if="formData.contributions.length > 1" class="mt-1.5 text-xs text-muted-foreground">This message will be sent to all {{ formData.contributions.length }} Virtual Assistants</p>
                <p v-else class="mt-1.5 text-xs text-muted-foreground">This message will be sent alongside your contribution</p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!equalDistribution" class="space-y-4">
          <div v-for="va in formData.contributions" :key="va.id" class="rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-md">
            <div class="space-y-4">
              <div class="flex items-center gap-3 border-b pb-3">
                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                  {{
                    va.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                  }}
                </div>
                <h3 class="text-lg font-semibold">{{ va.name }}</h3>
              </div>
              <div class="space-y-3">
                <div>
                  <Label class="mb-2 text-sm font-medium">Contribution Amount</Label>
                  <InputCurrency :id="`contribution-amount-${va.id}`" v-model="va.amount" :options="{ currency: 'USD' }" label="Contribution Amount" :disabled="busy" class="max-w-40" />
                </div>
                <div>
                  <Label class="mb-2 text-sm font-medium">Message to {{ va.name.split(' ')[0] }}</Label>
                  <Textarea v-model="va.comment" placeholder="Write a personal message to your Virtual Assistant..." :class="['h-24 resize-none', contributionsWithErrors.has(va.id) ? 'ring-2 ring-red-500 border-red-500' : '']" :disabled="busy" @input="clearContributionError(va.id)" />
                  <p class="mt-1.5 text-xs text-muted-foreground">This message will be sent alongside your contribution</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="my-8 rounded-lg border bg-accent/30 p-6 shadow-sm" data-testid="total_contribution">
          <div class="text-center">
            <p class="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Total Contribution</p>
            <div class="mb-1 text-3xl font-bold text-primary">{{ totalContributionAmount }}</div>
            <p class="text-sm text-muted-foreground">Split across {{ formData.contributions.length }} Virtual Assistant{{ formData.contributions.length !== 1 ? 's' : '' }}</p>
          </div>
        </div>
        <div class="rounded-lg border bg-card p-6 shadow-sm">
          <div class="space-y-3">
            <Label class="text-base font-semibold">Message to MyOutDesk</Label>
            <Textarea v-model="formData.notesToMod" aria-label="Message to MyOutDesk" placeholder="If you have any questions, comments, or concerns, please let us know." :disabled="busy" class="h-24" />
            <p class="text-xs text-muted-foreground">Optional: Share any additional context or questions with our team</p>
          </div>
        </div>
        <div class="mt-8 space-y-6">
          <Alert class="border-l-4 border-l-primary bg-primary/5">
            <AlertDescription class="text-base font-medium">
              <label class="flex items-start gap-3 cursor-pointer">
                <Checkbox v-model="formData.agreeToTerms" :required="true" :disabled="busy" class="mt-1"></Checkbox>
                <span class="leading-relaxed">I acknowledge by submitting this form I am authorizing MyOutDesk to charge my payment method on file for the amount specified.</span>
              </label>
            </AlertDescription>
          </Alert>
          <div class="flex items-center justify-center">
            <Button :disabled="busy" type="submit" size="lg">Submit</Button>
          </div>
          <div class="text-center text-sm text-muted-foreground">Please be patient - processing may take up to a minute when contributing to multiple Virtual Assistants.</div>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
</style>
